import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { useState } from "react";
import { CATALOGS } from '@/features/configuracion/catalogos/config/catalogs.config';
import type { CatalogKey } from '@/features/configuracion/catalogos/config/catalogs.config';
import CatalogListView from '@/features/configuracion/catalogos/components/CatalogListView';
import { Box, Settings2, Sparkles, UserCog, Warehouse, Tags } from "lucide-react";
import { userHasPermission } from '@/shared/lib/roleUtils';
import { useAuth } from '@/shared/hooks/useAuth';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import AlmacenesListView from '@/features/almacen/components/AlmacenesListView';
import CategoriasListView from '@/features/almacen/components/CategoriasListView';
import { useRevelado } from '@/shared/hooks/useRevelado';

const cards = Object.values(CATALOGS);

/**
 * Tarjeta de catálogo. Vive aparte porque cada una necesita su propio
 * observador de revelado (A-06) y dentro del map no se puede usar el hook.
 * El `transition-all` de antes salió: peleaba con la transición del revelado,
 * y la sombra y el borde del hover ya viajan en la clase .revelar.
 */
const TarjetaCatalogo = ({
  titulo,
  descripcion,
  icono,
  onClick,
}: {
  titulo: string;
  descripcion: string;
  icono: React.ReactNode;
  onClick: () => void;
}) => {
  const revelado = useRevelado<HTMLButtonElement>();

  return (
    <button
      ref={revelado.ref}
      type="button"
      onClick={onClick}
      style={revelado.style}
      className={`block p-6 bg-white rounded-xl border border-gray-200 hover:shadow-lg hover:border-purple-300 text-left flex justify-between items-center w-full ${revelado.className}`}
    >
      <div>
        <h3 className="text-lg font-semibold text-gray-800">{titulo}</h3>
        <p className="text-sm text-gray-500 mt-1">{descripcion}</p>
      </div>
      <div className="w-[47px] h-[47px] bg-[#E0E7FF] rounded-full flex items-center justify-center">
        {icono}
      </div>
    </button>
  );
};

const CatalogsPageSimple = () => {
  const { user, isLoading: authLoading } = useAuth();
  const [selectedCatalog, setSelectedCatalog] = useState<CatalogKey | null>(null);
  const [showAlmacenes, setShowAlmacenes] = useState(false);
  const [showCategorias, setShowCategorias] = useState(false);

  const renderIconCatalog = (catalog: string) => {
    if (!catalog) return <></>;
    switch (catalog) {
      case "areas":
        return <Settings2 className="w-[24px] h-[24px]" color="#357AF6" />;
      case "puestos":
        return <UserCog className="w-[24px] h-[24px]" color="#357AF6" />;
      case "origenes_candidato":
        return <Tags className="w-[24px] h-[24px]" color="#357AF6" />;
      case "almacenes":
        return <Warehouse className="w-[24px] h-[24px]" color="#357AF6" />;
      case "beneficios":
        return <Sparkles className="w-[24px] h-[24px]" color="#357AF6" />;
      case "categoriasProveedores":
        return <Box className="w-[24px] h-[24px]" color="#357AF6" />;
      default:
        break;
    }
  };

  // Las tarjetas se arman con los permisos del usuario. Mientras estos no
  // llegan, userHasPermission dice que no a todo y la pantalla se veia vacia,
  // como si no sirviera.
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-4 border-[#253579] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (showAlmacenes) {
    return <AlmacenesListView onBack={() => setShowAlmacenes(false)} />;
  }

  if (showCategorias) {
    return <CategoriasListView onBack={() => setShowCategorias(false)} />;
  }

  if (selectedCatalog) {
    return (
      <div>
        {userHasPermission(
          //`${selectedCatalog == 'categoriasProveedores' ? `proveedores.administracion.proveedores.consultar` : `catalogos.${selectedCatalog}.consultar`}`,
          `generales.catalogos.${selectedCatalog}.read`,
          user?.rbac?.effectivePermissions || []
        ) ? (
          <CatalogListView
            catalogKey={selectedCatalog}
            onClickBackButton={() => setSelectedCatalog(null)}
          />
        ) : (
          <div className="flex items-center justify-center min-h-screen">
            <PermissionsDenyPage />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <TituloDePantalla nivel="interna" className="mb-6">Catálogos</TituloDePantalla>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => {
          if (userHasPermission(
            `${c.permissions.read}`,
            user?.rbac?.effectivePermissions || []
          )) {
            return (
              <TarjetaCatalogo
                key={c.key}
                titulo={c.title}
                descripcion={`Gestionar ${c.title.toLowerCase()}`}
                icono={renderIconCatalog(c.key)}
                onClick={() => setSelectedCatalog(c.key)}
              />
            )
          }
        })}

        {/* Almacén — visible con permiso de catálogo de almacenes (ver o gestionar) */}
        {(userHasPermission("generales.catalogos.almacenes.ver", user?.rbac?.effectivePermissions || []) ||
          userHasPermission("generales.catalogos.almacenes.gestionar", user?.rbac?.effectivePermissions || [])) && (
          <TarjetaCatalogo
            titulo="Almacén"
            descripcion="Gestionar almacenes"
            icono={<Warehouse className="w-[24px] h-[24px]" color="#357AF6" />}
            onClick={() => setShowAlmacenes(true)}
          />
        )}

        {/* Categorías de herramientas — visible con permiso de ver herramientas */}
        {userHasPermission("almacen.gestionar.herramientas.ver", user?.rbac?.effectivePermissions || []) && (
          <TarjetaCatalogo
            titulo="Categorías de herramientas"
            descripcion="Gestionar categorías"
            icono={<Tags className="w-[24px] h-[24px]" color="#357AF6" />}
            onClick={() => setShowCategorias(true)}
          />
        )}
      </div>
    </div>
  );
};

export default CatalogsPageSimple;
